import { createSlice } from '@reduxjs/toolkit';

// EC動線コンテキスト。ec* 入口でセットし、確認画面（予約確定成功時）で参照・クリアする。
// リロード時に離脱制御（ロゴのリンク無効化・フッターFAQ非表示）が外れないようsessionStorage で保持する。
const SESSION_KEY = 'ecFlow';

const initialState = {
  fromEc: false, // EC動線か
  orderNo: null, // EC注文番号（ecOrderNo）
  tokCd: null, // 店舗コード（ecTokCd）
  carNo: null, // 車両連番（ecCarNo）
  meisaiNo: null, // EC明細番号（ecMeisaiNo）→EC戻りリンクの orderEntryPk
  callbackUrl: null, // EC戻り先URL（callbackUrl）。未指定時は env の固定URLへフォールバック
  startedAt: null, // EC動線開始時刻（epoch ms）。タイムアウトモーダル（ecTimeoutModal）の起点として使用
};

// リロードを跨いだ復元。sessionStorage 不可（プライベートモード等）は初期値にフォールバック
const loadEcFlow = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? { ...initialState, ...JSON.parse(raw) } : initialState;
  } catch {
    return initialState;
  }
};

const saveEcFlow = state => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage が使えない場合は握りつぶす（Redux 上の値のみで動作）
  }
};

const clearEcFlowStorage = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // no-op
  }
};

export const ecFlowSlice = createSlice({
  name: 'ecFlowSlice',
  initialState: loadEcFlow(),
  reducers: {
    // ec* 入口で毎回上書き（＝リセット）。sessionStorage にも反映しリロードを跨いで保持
    // ただし startedAt（EC動線開始時刻）は、同一EC動線（注文番号・明細番号が一致）への
    // 再dispatchでは維持する。dispatchEcFlow が画面遷移のたびに呼ばれても、その都度
    // Date.now() で上書きしてしまうとタイムアウト計測（ecTimeoutModal）の起点が
    // 延長され続けてしまうため。別の注文で入り直した場合（識別子が変わる場合）は
    // 新しい動線として開始時刻を採番し直す
    setEcFlow: (state, { payload }) => {
      const isSameEcFlow =
        state.fromEc &&
        !!state.startedAt &&
        state.orderNo === payload?.orderNo &&
        state.meisaiNo === payload?.meisaiNo;
      const next = {
        ...initialState,
        ...payload,
        // payload に startedAt が無い場合のフォールバック（呼び出し側の渡し忘れで
        // タイムアウト監視自体が動かなくなる方が影響が大きいため保険として残す）
        startedAt: isSameEcFlow
          ? state.startedAt
          : payload?.startedAt ?? Date.now(),
      };
      saveEcFlow(next);
      return next;
    },
    // 確認画面の予約確定成功時（EC動線）にクリア。sessionStorage も削除して制御を解除する
    clearEcFlow: () => {
      clearEcFlowStorage();
      return initialState;
    },
  },
  extraReducers: builder => {},
});

// EC戻りリンク（C4へのフォームPOST遷移。§11.2）は XHR ではなくブラウザの画面遷移そのもので
// あるため、本sliceでは扱わない。実装は `utils/ecTransitionUtil.js` の
// `transitionToEcOrderScreen` を参照（呼び出し元は `confirm.jsx`）。

export const ecFlowActions = ecFlowSlice.actions;
export default ecFlowSlice.reducer;
